
import { BlogPost } from '@/types/blog';
import { fetchBlogPosts } from './utils/fetchUtils';

/**
 * Server-side version of useBlogPosts that directly returns the posts data
 * without using React hooks. This is used for server-side rendering.
 */
export const getServerBlogPosts = async (params: {
  page?: number;
  perPage?: number;
  searchTerm?: string;
  category?: string | number | null;
  fetchAll?: boolean;
}): Promise<{
  posts: BlogPost[];
  error: string | null;
}> => {
  const { 
    page = 1, 
    perPage = 30, // Increased from 10 to 30 for better SEO coverage
    searchTerm = '', 
    category = null,
    fetchAll = false
  } = params;
  
  try {
    console.log(`[SSR] Loading blog posts - page: ${page}, perPage: ${perPage}, searchTerm: "${searchTerm}", category: ${category}, fetchAll: ${fetchAll}`);
    
    // Initial fetch
    const posts = await fetchBlogPosts(page, perPage, searchTerm, category);
    
    // If fetchAll is true, fetch all posts recursively until we get fewer posts than requested
    if (fetchAll && posts.length === perPage) {
      console.log(`[SSR] Fetching all posts for SEO, starting batch loading`);
      let allPosts = [...posts];
      let currentPage = page + 1;
      let hasMore = true;
      
      // Limit to a reasonable number of batches to prevent infinite loops
      const maxBatches = 10;
      let batchCount = 1;
      
      while (hasMore && batchCount < maxBatches) {
        console.log(`[SSR] Loading batch ${batchCount + 1}, page ${currentPage}`);
        const nextBatch = await fetchBlogPosts(currentPage, perPage, searchTerm, category);
        
        if (nextBatch.length > 0) {
          allPosts = [...allPosts, ...nextBatch];
          console.log(`[SSR] Added ${nextBatch.length} posts from batch ${batchCount + 1}, total: ${allPosts.length}`);
          
          // If we received fewer posts than requested, we've reached the end
          if (nextBatch.length < perPage) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
        
        currentPage++;
        batchCount++;
      }
      
      console.log(`[SSR] Completed batch loading, total posts: ${allPosts.length}`);
      return { posts: allPosts, error: null };
    }
    
    console.log(`[SSR] Successfully loaded ${posts.length} blog posts`);
    
    return { posts, error: null };
  } catch (err) {
    console.error('[SSR] Error loading blog posts:', err);
    return { posts: [], error: 'Error loading blog posts' };
  }
};
